/**
 * eventStore — イベント設定の永続管理
 *
 * 依存方向: UI → eventStore(Zustand) → EventRepository → localStorage
 * EventRepository は Zustand を直接参照しない（循環依存なし）
 */

import { create } from 'zustand';

// ──────────────────────────────────────────
// 型定義
// ──────────────────────────────────────────

export interface EventConfig {
  id: string;
  name: string;
  direction: 'en-ja' | 'ja-en';
  glossary: string[];
  createdAt: number;
  lastUsedAt: number;
  managementToken?: string;  // サーバー側発行の管理トークン（平文でlocalStorageに保存）
}

// ──────────────────────────────────────────
// EventRepository（localStorage操作、Zustand非依存）
// ──────────────────────────────────────────

const STORAGE_KEY = 'andiamo_events';
const MAX_EVENTS = 20;

interface StoredData {
  events: EventConfig[];
  activeEventId: string | null;
}

class LocalStorageEventRepository {
  private load(): StoredData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { events: [], activeEventId: null };
      return JSON.parse(raw) as StoredData;
    } catch {
      return { events: [], activeEventId: null };
    }
  }

  private save(data: StoredData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      console.error('[eventStore] localStorage save failed');
    }
  }

  listEvents(): EventConfig[] {
    return this.load().events;
  }

  getActiveEventId(): string | null {
    return this.load().activeEventId;
  }

  upsertEvent(event: EventConfig): void {
    const data = this.load();
    const idx = data.events.findIndex((e) => e.id === event.id);
    if (idx >= 0) {
      data.events[idx] = event;
    } else {
      if (data.events.length >= MAX_EVENTS) {
        data.events.sort((a, b) => a.lastUsedAt - b.lastUsedAt);
        data.events.shift();
      }
      data.events.push(event);
    }
    this.save(data);
  }

  deleteEvent(id: string): void {
    const data = this.load();
    data.events = data.events.filter((e) => e.id !== id);
    if (data.activeEventId === id) data.activeEventId = null;
    this.save(data);
  }

  setActiveEventId(id: string | null): void {
    const data = this.load();
    data.activeEventId = id;
    this.save(data);
  }
}

export const eventRepository = new LocalStorageEventRepository();

// ──────────────────────────────────────────
// Zustand Store（UI向けインターフェース）
// ──────────────────────────────────────────

interface EventStore {
  events: EventConfig[];
  activeEventId: string | null;

  load: () => void;
  upsertEvent: (event: EventConfig) => void;
  deleteEvent: (id: string) => void;
  setActiveEvent: (id: string) => void;
  clearActiveEvent: () => void;
  getActiveEvent: () => EventConfig | null;
}

export const useEventStore = create<EventStore>((set, get) => ({
  events: [],
  activeEventId: null,

  load: () => {
    const events = eventRepository.listEvents();
    const activeEventId = eventRepository.getActiveEventId();
    set({ events, activeEventId });
  },

  upsertEvent: (event) => {
    eventRepository.upsertEvent(event);
    set({ events: eventRepository.listEvents() });
  },

  deleteEvent: (id) => {
    eventRepository.deleteEvent(id);
    const activeEventId = eventRepository.getActiveEventId();
    set({ events: eventRepository.listEvents(), activeEventId });
  },

  setActiveEvent: (id) => {
    const events = eventRepository.listEvents();
    const event = events.find((e) => e.id === id);
    if (event) {
      eventRepository.upsertEvent({ ...event, lastUsedAt: Date.now() });
    }
    eventRepository.setActiveEventId(id);
    set({ events: eventRepository.listEvents(), activeEventId: id });
  },

  clearActiveEvent: () => {
    eventRepository.setActiveEventId(null);
    set({ activeEventId: null });
  },

  getActiveEvent: () => {
    const { events, activeEventId } = get();
    return events.find((e) => e.id === activeEventId) ?? null;
  },
}));
